// MaintenanceRequestUI.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Avatar,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
  FormControl,
  Chip,
  IconButton,
  CircularProgress,
  Pagination,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import {
  Email,
  Flag,
  GridView,
  NewReleases,
  Phone,
  Task,
  ViewList,
} from "@mui/icons-material";
import { formatDistanceToNow, parseISO } from "date-fns";
import axios from "axios";
import AssignProviderDialog from "../../components/AssignProviderDialog";
import { useUser } from "../../provider/AuthProvider";
import stringToColor from "../../services/stringToColor";
import { useTranslation } from "react-i18next";

// 1. Multi-language status labels
const STATUS_LABELS = {
  en: {
    URGENT: "Urgent",
    APPEAL: "Appeal",
    ASSIGNED:"Assigned",
    IN_PROGRESS: "In Progress",
    NEW: "New",
    APPROVED: "Approved",
    COMPLETED: "Completed",
    ASSIGNED: "Assigned",
  },
  ar: {
    URGENT: "عاجل",
    APPEAL: "استئناف",
    ASSIGNED:"مُكَلَّف",
    IN_PROGRESS: "قيد التنفيذ",
    NEW: "جديد",
    APPROVED: "موافق عليه",
    COMPLETED: "مكتمل",
    ASSIGNED: "تم التعيين",
  },
};

const STATUS_OPTIONS = [
  "URGENT",
  "APPEAL",
  "NEW",
  "ASSIGNED",
  "IN_PROGRESS",
  "APPROVED",
  "COMPLETED",
];

const DATE_OPTIONS = ["Today", "1 Week", "1 Month", "1 Year", "Custom"];

const colorMap = {
  URGENT: "#e53935",
  APPEAL: "#fb8c00",
  IN_PROGRESS: "#fdd835",
  NEW: "#42a5f5",
  APPROVED: "#43a047",
  COMPLETED: "#9e9e9e",
  ASSIGNED: "#F021E6",
};

const renderFilterGroup = (
  label,
  options,
  selectedValues,
  setSelectedValues,
  lang
) => {
  const allSelected =
    options.length > 0 && selectedValues.length === options.length;
  const partiallySelected = selectedValues.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedValues(allSelected ? [] : [...options]);
  };

  const toggleOne = (option) => {
    setSelectedValues((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  return (
    <Box mt={2}>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={partiallySelected}
              onChange={toggleAll}
            />
          }
          label={label}
        />
        <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={selectedValues.includes(option)}
                  onChange={() => toggleOne(option)}
                />
              }
              label={STATUS_LABELS[lang][option] || option}
            />
          ))}
        </Box>
      </FormGroup>
    </Box>
  );
};

const MaintenanceRequestUI = () => {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [buildingFilter, setBuildingFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState("Today");
  const [customDate, setCustomDate] = useState({ from: "", to: "" });

  const [buildings, setBuildings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("card");
  const itemsPerPage = 5;
  const { user } = useUser();

  // i18n
  const { i18n } = useTranslation();
  const lang = i18n.language && i18n.language.startsWith("ar") ? "ar" : "en";

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [reqRes, bldgRes, svcRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/maintenanceRequests/${user.id}`
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/buildings/${user.id}`
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/usableServiceTypeList/${user.id}`
        ),
      ]);
      setRequests(reqRes.data);
      setBuildings(bldgRes.data);
      setServices(svcRes.data);
    } catch (err) {
      console.error("Error fetching data", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async (serviceTypeName) => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/cmt/providersByServicePortal/${user.id}/${serviceTypeName}`
    );
    setAvailableProviders(res.data);
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line
  }, []);

  const filteredRequests = requests.filter((r) => {
    const matchStatus =
      statusFilter.length === 0 || statusFilter.includes(r.status);
    const matchBuilding =
      !buildingFilter || r.unit.building.name === buildingFilter;
    const matchService =
      serviceFilter.length === 0 ||
      serviceFilter.includes(r.compoundServiceType.name);
    return matchStatus && matchBuilding && matchService;
  });

  const urgentFirst = [...filteredRequests].sort((a, b) => {
    if (a.status === "URGENT") return -1;
    if (b.status === "URGENT") return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const paginated = urgentFirst.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const openAssignDialog = async (r) => {
    await fetchProviders(r.compoundServiceType.name);
    setSelectedProviderId("");
    setSelectedRequest(r);
    setAssignDialogOpen(true);
  };

  const confirmAssignment = async () => {
    if (!selectedRequest || !selectedProviderId) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/assignProvider`,
        {
          requestId: selectedRequest.id,
          providerId: selectedProviderId,
        }
      );
      setAssignDialogOpen(false);
      fetchInitialData();
    } catch (err) {
      console.error("Error assigning provider", err);
    }
  };

  // Card View
  const renderRequestCard = (r) => (
    <Paper
      key={r.id}
      sx={{
        p: 2,
        position: "relative",
        mb: 2,
        gap: 2,
        width: "70%",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {r.isUrgent && (
          <Chip
            icon={<Flag />}
            label={STATUS_LABELS[lang].URGENT}
            color="error"
            size="small"
          />
        )}
        {r.isAppeal && (
          <Chip
            icon={<NewReleases />}
            label={STATUS_LABELS[lang].APPEAL}
            color="error"
            size="small"
          />
        )}

        <Chip
          label={STATUS_LABELS[lang][r.status] || r.status}
          sx={{
            bgcolor: colorMap[r.status] || "default",
            color: "white",
            fontWeight: "bold",
          }}
          size="small"
        />
      </Box>
      <Box>
        <Box display="flex" justifyContent="space-between">
          <Box mt={3} ml={2}>
            <Typography variant="h5" fontWeight="bold" mb={1}>
              {r.compoundServiceType.name}
            </Typography>
            <Typography>{r.description}</Typography>
            <Typography variant="body2" color="text.secondary">
              {r.unit.building.name}, {r.unit.building.location} | Floor{" "}
              {r.unit.floor}, Flat {r.unit.flat_number}
            </Typography>
            {r.status === "COMPLETED" && (
              <Box display="flex" alignItems="center" mt={1} gap={1}>
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    sx={{
                      color: i < r.rating ? "#ffc107" : "#e0e0e0",
                      fontSize: 16,
                    }}
                  />
                ))}
                <Task />
                <Typography variant="caption">{r.rating}</Typography>
              </Box>
            )}
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignSelf="center"
            mt={3}
            mr={2}
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box>
                <Avatar
                  src={r.tenant.user?.avatar_url}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: stringToColor(r.tenant.user?.FullName),
                  }}
                  className="notranslate"
                >
                  {r.tenant.user?.FullName?.[0]?.toUpperCase() || ""}
                </Avatar>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="left"
                className="notranslate"
              >
                <Typography variant="caption" fontWeight="bold">
                  {r.tenant.user?.FullName}
                </Typography>
                <Typography variant="caption" fontWeight="bold">
                  {r.tenant.user?.email}
                </Typography>
                <Typography variant="caption" fontWeight="bold">
                  {r.tenant.user?.phone}
                </Typography>
              </Box>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1, bgcolor: "#444" }}
            />
            {r.serviceprovider && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box className="notranslate">
                  <Avatar
                    src={r.serviceprovider.user?.avatar_url}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: stringToColor(r.serviceprovider.user?.FullName),
                    }}
                  >
                    {r.serviceprovider.user?.FullName?.[0]?.toUpperCase() || ""}
                  </Avatar>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="left"
                  className="notranslate"
                >
                  <Typography variant="caption" fontWeight="bold">
                    {r.serviceprovider.user?.FullName}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {r.serviceprovider.user?.email}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold">
                    {r.serviceprovider.user?.phone}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box align="right" mt={3}>
          {(r.status === "NEW" || r.status === "REJECTED") && (
  <Tooltip title={"Assign the Service Provider"}>
    <Button onClick={() => openAssignDialog(r)}>
      {lang === "ar" ? "تعيين مزود الخدمة" : "Assign Provider"}
    </Button>
  </Tooltip>
)}

          {r.status !== "COMPLETED" && (
            <Typography variant="caption" color="info" display="block">
              {formatDistanceToNow(parseISO(r.createdAt))}{" "}
              {lang === "ar" ? "منذ" : "ago"}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  // Table View
  const renderRequestTable = (
    <TableContainer sx={{ maxHeight: "65vh" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <b>{lang === "ar" ? "الخدمة" : "Service"}</b>
            </TableCell>
            <TableCell>
              <b>{lang === "ar" ? "الحالة" : "Status"}</b>
            </TableCell>
            <TableCell>
              <b>{lang === "ar" ? "اسم المستأجر" : "Tenant Name"}</b>
            </TableCell>
            <TableCell>
              <b>{lang === "ar" ? "جهة الاتصال" : "Contact"}</b>
            </TableCell>
            <TableCell>
              <b>{lang === "ar" ? "تاريخ الطلب" : "Date"}</b>
            </TableCell>
            <TableCell align="right">
              <b>{lang === "ar" ? "إجراء" : "Action"}</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginated.map((r) => (
            <TableRow key={r.id}>
              <TableCell>
                <Typography variant="h6">
                  {r.compoundServiceType.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={STATUS_LABELS[lang][r.status] || r.status}
                  sx={{
                    bgcolor: colorMap[r.status] || "default",
                    color: "white",
                    fontWeight: "bold",
                  }}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Stack direction="column" spacing={1} alignItems="center">
                  <Avatar
                    src={r.tenant.user?.avatar_url}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: stringToColor(r.tenant.user?.FullName),
                    }}
                    className="notranslate"
                  >
                    {r.tenant.user?.FullName?.[0]?.toUpperCase() || ""}
                  </Avatar>
                  <Typography variant="h6" className="notranslate">
                    {r.tenant.user?.FullName}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  className="notranslate"
                >
                  <Email />
                  <Typography variant="h6">{r.tenant.user?.email}</Typography>
                </Box>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <Phone />
                  <Typography variant="h6" className="notranslate" dir="ltr">
                    {r.tenant.user?.phone}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {new Date(r.createdAt).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {r.status === "NEW" && (
                  <Tooltip
                    title={
                      lang === "ar" ? "تعيين مزود الخدمة" : "Assign Provider"
                    }
                  >
                    <IconButton
                      color="primary"
                      onClick={() => openAssignDialog(r)}
                      size="small"
                    >
                      <AssignmentIndIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // -------- RENDER ----------
  return (
    <Box className="main-container mt-4" ml={10}>
      <Grid container spacing={2} columns={{ xs: 2, sm: 6, md: 12 }}>
        <Grid item size={{ xs: 2, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, height: "75vh", overflowY: "auto" }} elevation={3}>
            <Typography fontWeight="bold" mb={1}>
              {lang === "ar" ? "تصفية الطلبات" : "Filter Requests"}
            </Typography>
            {renderFilterGroup(
              lang === "ar" ? "الحالة" : "Status",
              STATUS_OPTIONS,
              statusFilter,
              setStatusFilter,
              lang
            )}
            {renderFilterGroup(
              lang === "ar" ? "الخدمات" : "Services",
              services.map((s) => s.name),
              serviceFilter,
              setServiceFilter,
              lang
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{lang === "ar" ? "المبنى" : "Building"}</InputLabel>
              <Select
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e.target.value)}
                label={lang === "ar" ? "المبنى" : "Building"}
              >
                <MenuItem value="">{lang === "ar" ? "الكل" : "All"}</MenuItem>
                {buildings.map((b) => (
                  <MenuItem key={b.id} value={b.name}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{lang === "ar" ? "التاريخ" : "Date"}</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label={lang === "ar" ? "التاريخ" : "Date"}
              >
                {DATE_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {lang === "ar"
                      ? // Simple manual mapping for demo; for full i18n, add translations for these
                        d === "Today"
                        ? "اليوم"
                        : d === "1 Week"
                        ? "أسبوع"
                        : d === "1 Month"
                        ? "شهر"
                        : d === "1 Year"
                        ? "سنة"
                        : "مخصص"
                      : d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {dateFilter === "Custom" && (
              <Box mt={2}>
                <TextField
                  type="date"
                  label={lang === "ar" ? "من" : "From"}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  label={lang === "ar" ? "إلى" : "To"}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ mt: 1 }}
                />
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item size={{ xs: 2, sm: 6, md: 8 }}>
          <Paper sx={{ p: 2, height: "75vh" }} elevation={3}>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              mb={2}
            >
              <Tooltip title={lang === "ar" ? "عرض البطاقة" : "Card View"}>
                <IconButton
                  color={viewMode === "card" ? "primary" : "default"}
                  onClick={() => setViewMode("card")}
                  sx={{
                    border: viewMode === "card" ? "2px solid" : "none",
                    borderColor: "primary.main",
                    mr: 1,
                  }}
                >
                  <GridView />
                </IconButton>
              </Tooltip>
              <Tooltip title={lang === "ar" ? "عرض الجدول" : "Table View"}>
                <IconButton
                  color={viewMode === "table" ? "primary" : "default"}
                  onClick={() => setViewMode("table")}
                  sx={{
                    border: viewMode === "table" ? "2px solid" : "none",
                    borderColor: "primary.main",
                    mr: 1,
                  }}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Box>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ p: 2, height: "50vh" }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {filteredRequests.length > 0 ? (
                  <>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        height: "60vh",
                        flexDirection: "column",
                        overflowY: "auto",
                      }}
                    >
                      {viewMode === "card"
                        ? paginated.map(renderRequestCard)
                        : renderRequestTable}
                    </Box>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Pagination
                        count={Math.ceil(urgentFirst.length / itemsPerPage)}
                        page={currentPage}
                        onChange={(e, val) => setCurrentPage(val)}
                        color="primary"
                      />
                    </Box>
                  </>
                ) : (
                  <Typography align="center" mt={2}>
                    {lang === "ar" ? "لا توجد طلبات" : "No requests found."}
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <AssignProviderDialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          providers={availableProviders}
          selectedProviderId={selectedProviderId}
          setSelectedProviderId={setSelectedProviderId}
          selectedRequest={selectedRequest}
          onConfirm={confirmAssignment}
        />
      </Grid>
    </Box>
  );
};

export default MaintenanceRequestUI;
